import {useQueryClient} from '@tanstack/react-query';
import {paymentsApi} from '@/features/payments';
import {useProjectSchedulesFetcher, schedulesApi} from '@/features/schedules';
import {
  countSessionsMissingPrimaryModerator,
  countUpcomingModeratorSessions,
  countCancelledModeratorSessions,
  countNoShowFollowUpSessions,
  buildNoShowCancellationLimitEntries,
  summarizeModeratorActions,
  summarizeUnassignedActions,
  type ModeratorActionSummary,
  type NoShowLimitSessionType,
  type NoShowLimitEntry,
} from '@/shared/lib/schedule-moderators';
import {useBookingDecisionsFetcher} from '@/shared/hooks/useBookingDecisionsFetcher';
import {api} from '@/shared/services/api-client';
import type {MySessionChange} from '../components/action-center-cards';
import {queryKeys} from '@/lib/query-keys';
import {ACTION_CENTER_STALE_TIME_MS} from '@/config/cacheTtl';

/**
 * Shared cache for the Action Center's underlying numbers.
 *
 * The sidebar nav badge (mounted on every page, via `useActionCenterCount`)
 * and the Action Center page itself (`ActionCenter.tsx`) both need the same
 * per-project failed-payment count and schedule-derived metrics. Without a
 * shared cache each independently fires its own `payment-stats` request and
 * its own full, unpaginated schedule-list pull — the same waste this session
 * already fixed once for the Participants grid's demographics-table fetch
 * (see useDemographicsTableQuery.ts).
 *
 * The schedule list itself is fetched via `useProjectSchedulesFetcher`
 * (features/schedules) — the SAME shared cache entry that `Schedule.tsx` and
 * `TeamAvailability.tsx` read from, so switching between the Schedule page's
 * tabs and any page showing the Action Center badge doesn't re-pull the full
 * schedule list per consumer. Only the derived metrics are computed here;
 * they're cheap to recompute from an already-cached array, so there's no
 * separate cache layer for them.
 */

export interface ScheduleMetrics {
  noPrimaryModeratorCount: number;
  upcomingCount: number;
  cancelledCount: number;
  noShowFollowUpCount: number;
  moderatorSummaries: ModeratorActionSummary[];
  /** OD-1343: cancelled/no-show sessions that had no primary moderator. */
  unassignedSummary: {cancelledCount: number; noShowCount: number};
  /** OD-1102: (participant, session type) pairs at/over their no-show/cancellation limit. */
  noShowLimitCount: number;
  /** OD-1102: the actual flagged pairs, for Action Center's expanded decision panel. */
  noShowLimitEntries: NoShowLimitEntry[];
}

export const EMPTY_SCHEDULE_METRICS: ScheduleMetrics = {
  noPrimaryModeratorCount: 0,
  upcomingCount: 0,
  cancelledCount: 0,
  noShowFollowUpCount: 0,
  moderatorSummaries: [],
  unassignedSummary: {cancelledCount: 0, noShowCount: 0},
  noShowLimitCount: 0,
  noShowLimitEntries: [],
};

export function paymentStatsQueryKey(projectId: string) {
  return queryKeys.actionCenter.paymentStats(projectId);
}

/** Separate cache entry from paymentStatsQueryKey — this one is a different
 * server call (`bonus_flag=false`), not just a different read of the same
 * response, so it must not share a query key with the unfiltered stats. */
export function plainPendingPaymentStatsQueryKey(projectId: string) {
  return queryKeys.actionCenter.plainPendingPaymentStats(projectId);
}

export function bonusFlagsQueryKey(projectId: string) {
  return queryKeys.actionCenter.bonusFlags(projectId);
}

export function sessionTypesQueryKey(projectId: string) {
  return queryKeys.actionCenter.sessionTypes(projectId);
}

async function fetchFailedPaymentCountRaw(projectId: string): Promise<number> {
  const res = await paymentsApi.stats({project_id: projectId});
  return Number(res.data?.failed_count) || 0;
}

/** Pending requests with no bonus/partial-payment flag — the ones Action
 * Center's Bonus Requests / Partial Payment Requests cards don't already
 * cover, so this card's count never double-counts a row those two show. */
async function fetchPlainPendingPaymentCountRaw(projectId: string): Promise<number> {
  const res = await paymentsApi.stats({project_id: projectId, bonus_flag: false});
  return Number(res.data?.pending_count) || 0;
}

/** Session types for the project, keyed by id (OD-1102's no-show/cancellation
 * limit lives on the session type, not the schedule). */
async function fetchSessionTypesByIdRaw(
  projectId: string,
): Promise<Map<string, NoShowLimitSessionType>> {
  const res = await schedulesApi.listDetailedSessionTypesByProject(projectId);
  const sessionTypes = res.data?.data || res.data || [];
  const byId = new Map<string, NoShowLimitSessionType>();
  for (const sessionType of sessionTypes) {
    const id = sessionType?.id_guid ?? sessionType?.id;
    if (id != null) byId.set(String(id), sessionType);
  }
  return byId;
}

/** Every session ever flagged for bonus/partial-payment review (OD-1071),
 * any status. Reuses the same schedules-list endpoint and filter Payment
 * Approvals' "Bonus Requested" filter applies, so Action Center never needs
 * its own endpoint. Unfiltered by status (not just pending) so the card can
 * derive "N requests / N received this project" per moderator/participant
 * client-side without a second request. */
async function fetchBonusFlagRequestsRaw(projectId: string): Promise<any[]> {
  // OV2-75645 ledger migration: one row per payment CYCLE now, not per
  // session — a session can legitimately show up twice (e.g. a still-pending
  // Reschedule partial payment and a separately-flagged bonus). `.id` is
  // remapped to the payment_requests id so downstream per-row keys/counts
  // (buildPaymentRequestItems, the card's React list key) stay unique.
  const res = await paymentsApi.listPaymentRequests({
    project_id: projectId,
    bonus_flag: true,
    limit: 200,
  });
  const rows: any[] = res.data?.schedules ?? [];
  return rows.map((row) => ({ ...row, id: row.payment_request_id ?? row.id }));
}

/** The current moderator's own add/remove events on upcoming sessions in this
 * project. Self-scoped by the auth token; returns [] on any non-array payload. */
async function fetchMySessionChangesRaw(projectId: string): Promise<MySessionChange[]> {
  const res = await api.moderators.getMySessionChanges(projectId);
  return Array.isArray(res.data) ? (res.data as MySessionChange[]) : [];
}

export function availabilityApprovalsQueryKey(projectId: string) {
  return queryKeys.actionCenter.availabilityApprovals(projectId);
}

/** Project availability rows (any status). Callers filter to pending locally. */
async function fetchAvailabilityApprovalsRaw(projectId: string): Promise<any[]> {
  const res = await api.moderatorAvailability.list({project_id: projectId});
  return Array.isArray((res.data as any)?.data) ? (res.data as any).data : [];
}

function deriveScheduleMetrics(
  schedules: any[],
  userId: string | null,
  sessionTypesById: Map<string, NoShowLimitSessionType>,
): ScheduleMetrics {
  const nowMs = Date.now();
  const noShowLimitEntries = buildNoShowCancellationLimitEntries(schedules, sessionTypesById);
  return {
    noPrimaryModeratorCount: countSessionsMissingPrimaryModerator(schedules),
    upcomingCount: countUpcomingModeratorSessions(schedules, userId, nowMs),
    cancelledCount: countCancelledModeratorSessions(schedules, userId),
    noShowFollowUpCount: countNoShowFollowUpSessions(schedules, userId),
    moderatorSummaries: summarizeModeratorActions(schedules, nowMs),
    unassignedSummary: summarizeUnassignedActions(schedules),
    noShowLimitCount: noShowLimitEntries.length,
    noShowLimitEntries,
  };
}

/**
 * Imperative (not a `useQuery` hook) so callers keep full control over their
 * existing loading-state/error-handling code — this only replaces the raw
 * network calls with a cache-aware fetch. Pass `refresh: true` to bypass the
 * staleTime window and guarantee a fresh value (used by the nav badge, which
 * must recalc on every mount/tab-entry regardless of what's cached).
 */
export function useActionCenterDataFetchers() {
  const queryClient = useQueryClient();
  const {fetchProjectSchedules} = useProjectSchedulesFetcher();

  const fetchFailedPaymentCount = (
    projectId: string,
    options?: {refresh?: boolean},
  ) =>
    queryClient.fetchQuery({
      queryKey: paymentStatsQueryKey(projectId),
      queryFn: () => fetchFailedPaymentCountRaw(projectId),
      staleTime: options?.refresh ? 0 : ACTION_CENTER_STALE_TIME_MS,
    });

  /** PM-only "someone requested payment approval and it's still open" alert —
   * plain (unflagged) requests only, so it never double-counts a row the
   * Bonus/Partial Payment cards already surface. */
  const fetchPendingPaymentCount = (
    projectId: string,
    options?: {refresh?: boolean},
  ) =>
    queryClient.fetchQuery({
      queryKey: plainPendingPaymentStatsQueryKey(projectId),
      queryFn: () => fetchPlainPendingPaymentCountRaw(projectId),
      staleTime: options?.refresh ? 0 : ACTION_CENTER_STALE_TIME_MS,
    });

  const fetchBonusFlagRequests = (
    projectId: string,
    options?: {refresh?: boolean},
  ) =>
    queryClient.fetchQuery({
      queryKey: bonusFlagsQueryKey(projectId),
      queryFn: () => fetchBonusFlagRequestsRaw(projectId),
      staleTime: options?.refresh ? 0 : ACTION_CENTER_STALE_TIME_MS,
    });

  const fetchSessionTypesById = (
    projectId: string,
    options?: {refresh?: boolean},
  ) =>
    queryClient.fetchQuery({
      queryKey: sessionTypesQueryKey(projectId),
      queryFn: () => fetchSessionTypesByIdRaw(projectId),
      staleTime: options?.refresh ? 0 : ACTION_CENTER_STALE_TIME_MS,
    });

  const fetchScheduleMetrics = (
    projectId: string,
    userId: string | null,
    options?: {refresh?: boolean},
  ) =>
    Promise.all([
      fetchProjectSchedules(projectId, options),
      fetchSessionTypesById(projectId, options),
    ]).then(([schedules, sessionTypesById]) =>
      deriveScheduleMetrics(schedules, userId, sessionTypesById),
    );

  const fetchMySessionChanges = (
    projectId: string,
    options?: {refresh?: boolean},
  ) =>
    queryClient.fetchQuery({
      queryKey: queryKeys.actionCenter.mySessionChanges(projectId),
      queryFn: () => fetchMySessionChangesRaw(projectId),
      staleTime: options?.refresh ? 0 : ACTION_CENTER_STALE_TIME_MS,
    });

  const fetchAvailabilityApprovals = (
    projectId: string,
    options?: {refresh?: boolean},
  ) =>
    queryClient.fetchQuery({
      queryKey: availabilityApprovalsQueryKey(projectId),
      queryFn: () => fetchAvailabilityApprovalsRaw(projectId),
      staleTime: options?.refresh ? 0 : ACTION_CENTER_STALE_TIME_MS,
    });

  const {fetchBookingDecisions, saveBookingDecision, invalidateBookingDecisions} =
    useBookingDecisionsFetcher();

  return {
    fetchFailedPaymentCount,
    fetchPendingPaymentCount,
    fetchBonusFlagRequests,
    fetchScheduleMetrics,
    fetchMySessionChanges,
    fetchAvailabilityApprovals,
    fetchBookingDecisions,
    saveBookingDecision,
    invalidateBookingDecisions,
  };
}
